import { useState, useEffect } from 'react';
import { Printer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD } from '../utils/constants';
import { getFinancialYear } from './InvoiceModal';
import CreditNotePrint from './CreditNotePrint';
import { fetchCruiseNames, cruiseLabel } from '../utils/cruiseNames';

// ── Helpers ───────────────────────────────────────────────────────────────────
const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const f2 = (n) => `₹${Number(n || 0).toFixed(2)}`;
const fI = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

// Normalize a credit_notes row (+ its parent invoice + settings) into the shape
// CreditNotePrint expects. Used for both a fresh generate and a reprint, so the
// printed document is identical either way.
const buildCnData = (cn, invoice, settings, cruiseNames) => ({
  creditNoteNumber: cn.credit_note_number,
  creditNoteDate:   cn.credit_note_date,
  kind:             cn.kind,
  reason:           cn.reason,
  // Against-invoice reference — the CN copies context, but the number/date come
  // from the parent invoice row (the source of record).
  invoiceNumber:    invoice?.invoice_number,
  invoiceDate:      invoice?.invoice_date,
  invoiceType:      cn.invoice_type,
  customerName:     cn.customer_name,
  customerPhone:    invoice?.customer_phone || null,
  partner_gstin:      cn.partner_gstin,
  partner_legal_name: cn.partner_legal_name,
  partner_address:    cn.partner_address,
  partner_state:      cn.partner_state,
  partner_state_code: cn.partner_state_code,
  cruiseType: cn.cruise_type,
  // Guest-facing long name for the printed Cruise row (display only; the
  // credit_notes row keeps the stored cruise_type).
  cruiseDisplayName: cruiseLabel(cruiseNames) || cn.cruise_type,
  deck:       cn.deck,
  adults:     cn.adults,
  children:   cn.children,
  taxableCruise: cn.taxable_amount_cruise,
  cgstCruise:    cn.cgst_cruise,
  sgstCruise:    cn.sgst_cruise,
  taxableFood:   cn.taxable_amount_food,
  cgstFood:      cn.cgst_food,
  sgstFood:      cn.sgst_food,
  totalTax:      cn.total_tax,
  grandTotal:    cn.grand_total,
  settings,
});

// ── Main component ────────────────────────────────────────────────────────────
export default function CreditNoteModal({ booking, invoice, onClose, onSaved }) {
  const [existingCns, setExistingCns] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [settings, setSettings]       = useState(null);
  // The `invoice` PROP may be a PARTIAL row (the caller's invoiceMap selects only
  // a few columns), so we fetch the COMPLETE row on open and read every field
  // (grand_total, components, copied context) from it. The prop is used only for .id.
  const [fullInvoice, setFullInvoice] = useState(null);
  const [cruiseNames, setCruiseNames] = useState(null);  // display names row for the printed CN

  const [kind, setKind]               = useState('full');       // 'full' | 'partial'
  const [partialAmount, setPartialAmount] = useState('');
  const [reason, setReason]           = useState('');
  const [cnDate, setCnDate]           = useState(todayInput());

  const [generating, setGenerating]   = useState(false);
  const [error, setError]             = useState('');
  const [printCN, setPrintCN]         = useState(null);         // creditNoteData for CreditNotePrint

  const invoiceGrandTotal = Number(fullInvoice?.grand_total) || 0;

  // Fetch the FULL invoice row + existing CNs + invoice settings, all in parallel
  // (each only needs invoice.id). The cap math never runs on partial data because
  // the form is gated on fullInvoice below.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    Promise.all([
      supabase.from('invoices').select('*').eq('id', invoice.id).single(),
      supabase.from('credit_notes').select('*').eq('invoice_id', invoice.id).order('created_at'),
      supabase.from('invoice_settings').select('*').limit(1).maybeSingle(),
    ]).then(async ([invRes, cnRes, setRes]) => {
      if (!alive) return;
      if (invRes.error || !invRes.data) {
        setError('Failed to load invoice: ' + (invRes.error?.message || 'invoice not found'));
        setLoading(false);
        return;
      }
      // Display names for the printed Cruise row; null falls back to the stored name.
      const names = await fetchCruiseNames({ cruiseName: invRes.data.cruise_type });
      if (!alive) return;
      setCruiseNames(names);
      setFullInvoice(invRes.data);
      setExistingCns(cnRes.data || []);
      setSettings(setRes.data || {});
      setLoading(false);
    });
    return () => { alive = false; };
  }, [invoice.id]);

  // ── Cap / cumulative logic ────────────────────────────────────────────────
  // Cumulative reversed grand_total across ALL CNs for this invoice must never
  // exceed the invoice's own grand_total.
  const alreadyReversed = existingCns.reduce((s, c) => s + (Number(c.grand_total) || 0), 0);
  const remaining       = Math.max(0, invoiceGrandTotal - alreadyReversed);
  const fullyReversed   = remaining <= 0.005; // paise-tolerance

  // Reversal amount R for the current form state.
  const partialNum = Number(partialAmount);
  const reversalAmount = kind === 'full'
    ? remaining
    : (Number.isFinite(partialNum) ? partialNum : 0);

  // ── Proportional split ─────────────────────────────────────────────────────
  // Scale EVERY component of the original invoice by ratio = R / invoice.grand_total.
  const ratio = invoiceGrandTotal > 0 ? reversalAmount / invoiceGrandTotal : 0;
  const split = {
    taxable_amount_cruise: (Number(fullInvoice?.taxable_amount_cruise) || 0) * ratio,
    cgst_cruise:           (Number(fullInvoice?.cgst_cruise)           || 0) * ratio,
    sgst_cruise:           (Number(fullInvoice?.sgst_cruise)           || 0) * ratio,
    taxable_amount_food:   (Number(fullInvoice?.taxable_amount_food)   || 0) * ratio,
    cgst_food:             (Number(fullInvoice?.cgst_food)             || 0) * ratio,
    sgst_food:             (Number(fullInvoice?.sgst_food)             || 0) * ratio,
    total_tax:             (Number(fullInvoice?.total_tax)             || 0) * ratio,
    grand_total:           reversalAmount, // grand_total = R exactly
  };
  const hasFood = ((Number(fullInvoice?.taxable_amount_food) || 0) + (Number(fullInvoice?.cgst_food) || 0) + (Number(fullInvoice?.sgst_food) || 0)) > 0;

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    if (!reason.trim()) return 'A reason is required.';
    if (!cnDate)        return 'Credit note date is required.';
    if (remaining <= 0) return 'This invoice is already fully reversed.';
    if (kind === 'partial') {
      if (!Number.isFinite(partialNum) || partialNum <= 0) return 'Enter a valid amount greater than 0.';
      if (partialNum > remaining + 0.005) return `Amount exceeds the remaining reversible balance (${fI(remaining)}).`;
    }
    return '';
  };

  const handleGenerate = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setGenerating(true);
    setError('');

    // Allocate the per-day CN sequence (advisory-locked, resets daily).
    const { data: seq, error: seqErr } = await supabase
      .rpc('allocate_credit_note_number', { p_cn_date: cnDate });
    if (seqErr || seq == null) {
      setGenerating(false);
      setError('Failed to allocate credit note number: ' + (seqErr?.message || 'no sequence returned'));
      return;
    }

    // CN number: BCPL/CN/YYMMDD/NNNN (yymmdd derived from the CN date).
    const [yStr, mStr, dStr] = cnDate.split('-');
    const yymmdd = `${yStr.slice(2)}${mStr}${dStr}`;
    const creditNoteNumber = `BCPL/CN/${yymmdd}/${String(seq).padStart(4, '0')}`;

    const payload = {
      credit_note_number: creditNoteNumber,
      credit_note_date:   cnDate,
      sequence_number:    seq,
      financial_year:     getFinancialYear(cnDate),
      // Linkage
      invoice_id: invoice.id,
      booking_id: booking.id,
      // Kind + reason
      kind,
      reason: reason.trim(),
      // Reversed component split (proportional)
      taxable_amount_cruise: split.taxable_amount_cruise,
      cgst_cruise:           split.cgst_cruise,
      sgst_cruise:           split.sgst_cruise,
      taxable_amount_food:   split.taxable_amount_food,
      cgst_food:             split.cgst_food,
      sgst_food:             split.sgst_food,
      total_tax:             split.total_tax,
      grand_total:           split.grand_total,
      // Context copied FROM THE INVOICE (the source of record), not the booking.
      invoice_type:       fullInvoice.invoice_type,
      customer_name:      fullInvoice.customer_name,
      partner_id:         fullInvoice.partner_id,
      partner_gstin:      fullInvoice.partner_gstin,
      partner_legal_name: fullInvoice.partner_legal_name,
      partner_address:    fullInvoice.partner_address,
      partner_state:      fullInvoice.partner_state,
      partner_state_code: fullInvoice.partner_state_code,
      cruise_type:        fullInvoice.cruise_type,
      deck:               fullInvoice.deck,
      adults:             fullInvoice.adults,
      children:           fullInvoice.children,
    };

    const { data: inserted, error: saveErr } = await supabase
      .from('credit_notes').insert(payload).select().single();

    setGenerating(false);
    if (saveErr) { setError('Failed to save: ' + saveErr.message); return; }

    const cnRow = inserted || payload;
    setExistingCns(prev => [...prev, cnRow]);
    if (onSaved) onSaved(cnRow);
    setPrintCN(buildCnData(cnRow, fullInvoice, settings, cruiseNames));
  };

  // Reprint an existing CN.
  const openReprint = (cn) => setPrintCN(buildCnData(cn, fullInvoice, settings, cruiseNames));

  const inp = {
    width: '100%', padding: '8px 12px',
    border: '1.5px solid #d8d0c0', borderRadius: 8,
    fontSize: '0.88rem', outline: 'none',
    boxSizing: 'border-box', background: '#fff', color: NAVY,
  };
  const lbl = { display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#5a4a38', marginBottom: 4 };

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16, overflowY: 'auto' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 500, padding: '24px', boxShadow: '0 8px 40px rgba(10,22,40,0.22)', maxHeight: '92vh', overflowY: 'auto' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: NAVY }}>Issue Credit Note</h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#9a8e7a' }}>
                {fullInvoice ? `Against ${fullInvoice.invoice_number} · ${fI(invoiceGrandTotal)}` : 'Loading invoice…'}
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#9a8e7a', padding: '0 4px', lineHeight: 1 }}>×</button>
          </div>

          {loading ? (
            <p style={{ color: '#9a8e7a', fontSize: '0.82rem' }}>Loading invoice…</p>
          ) : !fullInvoice ? (
            <>
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: 18 }}>
                {error || 'Could not load the invoice for this credit note.'}
              </p>
              <button
                onClick={onClose}
                style={{ width: '100%', background: NAVY, color: GOLD, border: 'none', borderRadius: 8, padding: '10px 0', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Close
              </button>
            </>
          ) : (
            <>
              {/* Prior CNs summary */}
              {existingCns.length > 0 && (
                <div style={{ background: '#f8f6f1', border: '1px solid #e8e0d0', borderRadius: 10, padding: '12px 16px', marginBottom: 18 }}>
                  <div style={{ fontSize: '0.71rem', fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                    Already reversed: {fI(alreadyReversed)} of {fI(invoiceGrandTotal)}
                  </div>
                  {existingCns.map((c) => (
                    <div key={c.id || c.credit_note_number} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.credit_note_number}</div>
                        <div style={{ fontSize: '0.72rem', color: '#7a6a50' }}>{c.credit_note_date} · {c.reason}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY }}>{fI(c.grand_total)}</span>
                        <button
                          onClick={() => openReprint(c)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid #d8d0c0', borderRadius: 6, padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600, color: '#5a4a38', cursor: 'pointer' }}
                        >
                          <Printer size={13} strokeWidth={2} aria-hidden="true" /> Print
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {fullyReversed ? (
                <p style={{ color: NAVY, fontSize: '0.85rem', fontWeight: 600, marginBottom: 18 }}>
                  This invoice is fully reversed. No further credit note can be issued.
                </p>
              ) : (
                <>
                  {/* Kind */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={lbl}>Reversal Type</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {[
                        ['full',    `Full (${fI(remaining)})`],
                        ['partial', 'Partial'],
                      ].map(([val, label]) => (
                        <button
                          key={val}
                          onClick={() => setKind(val)}
                          style={{
                            flex: 1, padding: '9px 0', borderRadius: 8, cursor: 'pointer',
                            fontSize: '0.83rem', fontWeight: 700,
                            border: `1.5px solid ${kind === val ? GOLD : '#d8d0c0'}`,
                            background: kind === val ? '#fdf7e6' : '#fff',
                            color: kind === val ? '#5a4a10' : '#5a4a38',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Partial amount */}
                  {kind === 'partial' && (
                    <div style={{ marginBottom: 14 }}>
                      <label style={lbl}>Amount to Reverse (max {fI(remaining)})</label>
                      <input
                        type="number" min="0" step="0.01" value={partialAmount}
                        onChange={e => setPartialAmount(e.target.value)}
                        placeholder="0.00"
                        style={inp}
                      />
                    </div>
                  )}

                  {/* Reason */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={lbl}>Reason</label>
                    <input
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      placeholder="e.g. Booking cancelled / Pax reduced 4 → 2"
                      style={inp}
                    />
                  </div>

                  {/* CN date */}
                  <div style={{ marginBottom: 18 }}>
                    <label style={lbl}>Credit Note Date</label>
                    <input
                      type="date" value={cnDate}
                      onChange={e => setCnDate(e.target.value)}
                      style={{ ...inp, colorScheme: 'light' }}
                    />
                  </div>

                  {/* Preview */}
                  <div style={{ border: '1px solid #e8e0d0', borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
                    <div style={{ background: NAVY, padding: '9px 14px', fontSize: '0.71rem', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Credit Note Preview
                    </div>
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ fontSize: '0.79rem', color: '#6b7280' }}>Against Invoice</span>
                        <span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{fullInvoice.invoice_number}{fullInvoice.invoice_date ? ` · ${fullInvoice.invoice_date}` : ''}</span>
                      </div>
                      <div style={{ height: 1, background: '#e8e0d0', margin: '8px 0' }} />
                      <div style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a50', marginBottom: 5 }}>Cruise Charges</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ fontSize: '0.79rem', color: '#6b7280' }}>Taxable</span><span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{f2(split.taxable_amount_cruise)}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ fontSize: '0.79rem', color: '#6b7280' }}>CGST</span><span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{f2(split.cgst_cruise)}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ fontSize: '0.79rem', color: '#6b7280' }}>SGST</span><span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{f2(split.sgst_cruise)}</span></div>
                      </div>
                      {hasFood && (
                        <>
                          <div style={{ height: 1, background: '#e8e0d0', margin: '6px 0' }} />
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a50', marginBottom: 5 }}>Food &amp; Beverage</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ fontSize: '0.79rem', color: '#6b7280' }}>Taxable</span><span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{f2(split.taxable_amount_food)}</span></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ fontSize: '0.79rem', color: '#6b7280' }}>CGST</span><span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{f2(split.cgst_food)}</span></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ fontSize: '0.79rem', color: '#6b7280' }}>SGST</span><span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{f2(split.sgst_food)}</span></div>
                          </div>
                        </>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ fontSize: '0.79rem', color: '#6b7280' }}>Total Tax Reversed</span>
                        <span style={{ fontSize: '0.79rem', fontWeight: 600, color: NAVY }}>{f2(split.total_tax)}</span>
                      </div>
                      <div style={{ height: 1, background: '#e8e0d0', margin: '8px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: NAVY }}>Total Credited ({kind === 'full' ? 'Full' : 'Partial'})</span>
                        <span style={{ fontSize: '0.88rem', fontWeight: 800, color: GOLD }}>{fI(split.grand_total)}</span>
                      </div>
                    </div>
                  </div>

                  {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: 12 }}>{error}</p>}

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={handleGenerate}
                      disabled={generating || !settings}
                      style={{
                        flex: 1, background: NAVY, color: GOLD, border: 'none', borderRadius: 8,
                        padding: '10px 0', fontSize: '0.88rem', fontWeight: 700,
                        cursor: (generating || !settings) ? 'not-allowed' : 'pointer',
                        opacity: (generating || !settings) ? 0.7 : 1,
                      }}
                    >
                      {generating ? 'Issuing…' : 'Issue Credit Note'}
                    </button>
                    <button
                      onClick={onClose}
                      style={{ flex: 1, background: 'none', color: '#5a4a38', border: '1px solid #d8d0c0', borderRadius: 8, padding: '10px 0', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {fullyReversed && (
                <button
                  onClick={onClose}
                  style={{ width: '100%', background: NAVY, color: GOLD, border: 'none', borderRadius: 8, padding: '10px 0', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}
                >
                  Close
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {printCN && (
        <CreditNotePrint creditNoteData={printCN} onClose={() => setPrintCN(null)} />
      )}
    </>
  );
}
